import { Button } from '@/components/ui/Button';

interface ConfigurationStepProps {
  runName: string;
  description: string;
  concurrency: number;
  multiplicity: number;
  totalExecutions: number;
  placeholderName: string;
  onRunNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onConcurrencyChange: (value: number) => void;
  onMultiplicityChange: (value: number) => void;
  onBack: () => void;
  onExecute: () => void;
  isSubmitting: boolean;
}

export function ConfigurationStep({
  runName,
  description,
  concurrency,
  multiplicity,
  totalExecutions,
  placeholderName,
  onRunNameChange,
  onDescriptionChange,
  onConcurrencyChange,
  onMultiplicityChange,
  onBack,
  onExecute,
  isSubmitting,
}: ConfigurationStepProps) {
  return (
    <>
      {/* Run Name */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1A1A1A' }}
        >
          Run Name (optional)
        </label>
        <input
          type="text"
          value={runName}
          onChange={e => onRunNameChange(e.target.value)}
          placeholder={placeholderName}
          className="w-full px-3 py-3 rounded-md text-base"
          style={{ border: '1px solid #E8E0D8' }}
        />
        <div className="text-xs mt-1" style={{ color: '#A0A0A0' }}>
          Leave empty to auto-generate
        </div>
      </div>

      {/* Description */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1A1A1A' }}
        >
          Description (optional)
        </label>
        <textarea
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
          rows={3}
          placeholder="What is this run testing?"
          className="w-full px-3 py-3 rounded-md text-base font-inherit resize-y"
          style={{ border: '1px solid #E8E0D8' }}
        />
      </div>

      {/* Concurrency */}
      <div className="mb-5">
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1A1A1A' }}
        >
          Concurrency: {concurrency}
        </label>
        <input
          type="range"
          min="1"
          max="10"
          value={concurrency}
          onChange={e => onConcurrencyChange(parseInt(e.target.value))}
          className="w-full"
          style={{ accentColor: '#8B2332' }}
        />
        <div
          className="flex justify-between text-xs mt-1"
          style={{ color: '#A0A0A0' }}
        >
          <span>1 (Sequential)</span>
          <span>10 (Max Parallel)</span>
        </div>
      </div>

      {/* Multiplicity */}
      <div className="mb-8">
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1A1A1A' }}
        >
          Multiplicity
        </label>
        <input
          type="number"
          min="1"
          max="100"
          value={multiplicity}
          onChange={e =>
            onMultiplicityChange(Math.max(1, parseInt(e.target.value) || 1))
          }
          className="w-full px-3 py-3 rounded-md text-base"
          style={{ border: '1px solid #E8E0D8' }}
        />
        <div className="text-xs mt-1" style={{ color: '#A0A0A0' }}>
          Run each (prompt × config) combination this many times
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button variant="secondary" onClick={onBack} disabled={isSubmitting}>
          ← Back
        </Button>
        <Button variant="primary" onClick={onExecute} disabled={isSubmitting}>
          {isSubmitting
            ? 'Starting...'
            : `Start Run (${totalExecutions} executions)`}
        </Button>
      </div>
    </>
  );
}
